import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {AxiosError} from 'axios';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import Modal from 'patient-ping-remedy/packages/modal';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import Spinner from 'patient-ping-remedy/packages/spinner';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import AvatarDetails from '@common/AvatarDetails';
import {resolveSenderUser}
  from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContactSmsEvent';
import {
  ConnectContactResponse,
  ConnectContactSmsEvent,
  ConnectContactSmsEventRole,
  SMS_CONVERSATION_ACTIVE,
} from '@/api/dto/connect';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import {
  Container,
  Disclaimer,
  DisclaimerText,
  DisclaimerDismiss,
  SyncStatus,
  Thread,
  EmptyThread,
  Bubble,
  BubbleSender,
  BubbleContent,
  BubbleTime,
  Composer,
  ComposerRow,
  ComposerActions,
  ComposerInput,
  CharCount,
} from '@/components/pages/tasks/aws_communications/AwsCommunicationsSmsConversationModal.styles';

const POLL_INTERVAL_MS = 5_000;

// Hard cap on a single reply. Not a GSM segment limit (SMS concatenates), just a sane ceiling so
// a navigator can't paste an essay that fans out into many billed segments.
const MAX_SMS_LENGTH = 1000;

type Props = {
  isOpen: boolean;
  close(): void;
  connectContact: ConnectContactResponse;
};

// A message originated by us (the navigator's/system's side) renders on the right; the patient's
// inbound messages render on the left.
function isOwnMessage(role: ConnectContactSmsEventRole) {
  return role !== ConnectContactSmsEventRole.CUSTOMER;
}

// Sender label: prefer the known user's name; only fall back to "(BOT)" when there is genuinely no
// associated user (an automated/event-driven send). See CARECO-5791 attribution decision.
function senderLabel(event: ConnectContactSmsEvent) {
  if (event.senderName) {
    return event.senderName;
  }
  if (event.role === ConnectContactSmsEventRole.BOT) {
    return '(BOT)';
  }
  return DisplayHelpers.humanize(event.role);
}

export default function AwsCommunicationsSmsConversationModal({ isOpen, close, connectContact }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { currentUser } = useAuthStore();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  // The message we just sent and are waiting to see reconcile back from the CTR pipeline. While
  // set, the composer is locked -- Tier 2 is turn-based and we do not let a second send race the
  // CTR (which would hit Connect's ConflictException anyway). We record the own-message count at
  // send time so reconcile only matches a message that arrives AFTER the send -- otherwise an
  // earlier identical message (e.g. "Ok") would clear the lock immediately. Cleared once the sent
  // text shows up as a newly-arrived own message in a polled thread response (or on failure).
  const [pendingSend, setPendingSend] = useState<{ body: string; ownCount: number } | null>(null);
  const [sending, setSending] = useState(false);
  // Synchronous guard against a fast double-submit: `locked`/`sending` are render state and two
  // submits in the same tick both see the stale value, so both would reach replySms. The ref
  // flips immediately.
  const inFlight = useRef(false);
  // Monotonic token for the current modal open/reset. A send captures it; if the modal is closed
  // and reopened before replySms settles, the token advances and the stale completion is ignored
  // (so it can't clear a new draft, render the old body as pending, or wedge inFlight).
  const sessionRef = useRef(0);
  // Set true once a sent reply has reconciled during this session, so the panel contacts query is
  // refreshed when the user closes the modal (not mid-session -- see the reconcile effect).
  const panelStaleRef = useRef(false);
  // Set when a send is rejected because a session is currently open (409 CONVERSATION_ACTIVE), or
  // pre-emptively hinted by the thread. Blocks the composer with an honest message.
  const [conversationActive, setConversationActive] = useState(false);
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(false);

  const { data: thread, isLoading } = useQuery({
    queryKey: ['connect', 'sms', 'thread', connectContact.id],
    queryFn: () => carecoApi?.getSmsThread(connectContact.id),
    enabled: !!carecoApi && isOpen,
    refetchInterval: isOpen ? POLL_INTERVAL_MS : false,
  });

  const messages = useMemo(() => thread?.messages ?? [], [thread]);

  // Reset transient state when the modal opens/closes. Keyed on isOpen only -- NOT connectContact.id
  // -- because a CTR sync can swap the collapsed card's representative contact (new latest message)
  // and thus change connectContact.id mid-session; that must not reset the open conversation. Each
  // conversation has its own card instance (keyed on phone number in the drawer), so this modal only
  // ever represents one conversation. See CARECO-5791.
  useEffect(() => {
    sessionRef.current += 1;
    inFlight.current = false;
    if (isOpen) {
      setDraft('');
      setPendingSend(null);
      setSending(false);
      setConversationActive(false);
      setDisclaimerDismissed(false);
      panelStaleRef.current = false;
    }
  }, [isOpen]);

  // Close handler: if a reply synced during this session, refresh the panel now (on manual close)
  // so its card preview reflects the latest message. Deferring to close -- instead of invalidating
  // on reconcile -- avoids re-collapsing the contacts list and unmounting this modal mid-session.
  const handleClose = () => {
    if (panelStaleRef.current) {
      panelStaleRef.current = false;
      void queryClient.invalidateQueries({ queryKey: ['connect', 'contacts'] });
    }
    close();
  };

  // Reconcile: unlock once OUR sent message appears as a newly-arrived own message (beyond the
  // count that existed when we sent), not an older identical one.
  useEffect(() => {
    if (pendingSend) {
      const own = messages.filter((m) => isOwnMessage(m.role));
      if (own.length > pendingSend.ownCount
        && own.slice(pendingSend.ownCount).some((m) => m.content === pendingSend.body)) {
        setPendingSend(null);
        // A reply synced into a new contact, so the panel's card preview is now stale. Defer the
        // refresh to close time (see handleClose): invalidating here would refetch the contacts
        // list, which re-collapses to the new latest contact and unmounts THIS modal's host
        // component, closing the modal out from under the user. We want manual close only.
        panelStaleRef.current = true;
      }
    }
  }, [messages, pendingSend]);

  const threadRef = useRef<HTMLDivElement>(null);
  // Keep the thread pinned to the latest message. Runs on open, after the thread finishes loading,
  // and whenever messages change. rAF defers the scroll until after the bubbles have painted, so
  // we measure the final scrollHeight (not a stale one from before render).
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (threadRef.current) {
        threadRef.current.scrollTop = threadRef.current.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, isLoading, messages.length, pendingSend]);

  const locked = sending || !!pendingSend || conversationActive;

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || locked || !carecoApi || inFlight.current) {
      return;
    }
    const sendSession = sessionRef.current;
    inFlight.current = true;
    setSending(true);
    try {
      await carecoApi.replySms(connectContact.id, body);
      // Ignore completion if the modal was closed/reopened mid-flight.
      if (sendSession !== sessionRef.current) {
        return;
      }
      setDraft('');
      setConversationActive(false);
      // Lock until this message reconciles from the CTR round-trip. Snapshot the current own-
      // message count so reconcile only matches a message that arrives after this send.
      setPendingSend({ body, ownCount: messages.filter((m) => isOwnMessage(m.role)).length });
    } catch (error) {
      if (sendSession !== sessionRef.current) {
        return;
      }
      const axiosError = error as AxiosError<{ reason?: string; message?: string }>;
      if (axiosError.response?.status === 409
        && axiosError.response?.data?.reason === SMS_CONVERSATION_ACTIVE) {
        // Honest "conversation active" UX: do not lock the composer, keep the draft so the user can
        // retry once it settles.
        setConversationActive(true);
      } else {
        Helpers.alertOnError(`Failed to send reply. ${GENERICERROR} ${Helpers.traceId(axiosError)}`, axiosError);
      }
    } finally {
      // Only clear guards for the still-current session; a reopened session already reset them.
      if (sendSession === sessionRef.current) {
        inFlight.current = false;
        setSending(false);
      }
    }
  };

  const nationalNumber = connectContact.phoneNumber
    ? parsePhoneNumberFromString(connectContact.phoneNumber)?.nationalNumber
    : undefined;
  const phoneNumber = DisplayHelpers.formatPhoneNumber(nationalNumber);

  // Resolve the title from a single client object, never a field-by-field blend of the two
  // sources. thread.client is authoritative (server-stitched, from the latest contact's
  // association); connectContact.client is only a fallback when the thread has no client yet.
  // Mixing fields let a legitimately-null middle name fall through (??) to the other source's
  // fallback name (e.g. the "Unknown Caller" lambda default), producing a Frankenstein title
  // like "Adalberto Caller Fisher". See CARECO-5791.
  const titleClient = thread?.client?.firstName
    ? thread.client
    : connectContact.client.firstName
      ? connectContact.client
      : null;
  const headerName = titleClient
    ? DisplayHelpers.formatName(
      titleClient.firstName,
      titleClient.middleName,
      titleClient.lastName,
    )
    : phoneNumber;

  const ownName = currentUser
    ? DisplayHelpers.formatName(currentUser.first_name, null, currentUser.last_name)
    : undefined;

  // Optimistic bubble for a message that has been sent but not yet reconciled from the CTR round
  // trip. Rendered as our side with the current user's name so it visually matches what the CTR
  // will later capture (role BOT + senderName), and de-dupes cleanly on reconcile.
  const optimisticBubble = pendingSend
    ? (
      <Bubble own key="__pending__">
        <BubbleSender own>{ownName ?? '(BOT)'}</BubbleSender>
        <BubbleContent own className={css({ opacity: 0.6 })}>{pendingSend.body}</BubbleContent>
        <BubbleTime own>Sending...</BubbleTime>
      </Bubble>
    )
    : null;

  return (
    <Modal
      isOpen={isOpen}
      headerText={`Conversation with ${headerName}`}
      className={css({ width: '60rem' })}
      hideModal={handleClose}
    >
      <Container>
        {!disclaimerDismissed && (
          <Disclaimer>
            <DisclaimerText>
              This is not a real-time chat: sent replies can take up to a couple of minutes to
              appear, and Reply is locked until yours syncs (prevents confusion and duplicate
              sends). We recommend sending longer, complete messages.
            </DisclaimerText>
            <DisclaimerDismiss
              aria-label="Dismiss notice"
              onClick={() => setDisclaimerDismissed(true)}
            >
              <Icon iconClass={ICONS.times} weight="far" />
            </DisclaimerDismiss>
          </Disclaimer>
        )}
        {!!pendingSend && (
          <SyncStatus>
            <Spinner iconSize="sm" />
            Waiting for your message to sync. This can take up to a couple of minutes.
          </SyncStatus>
        )}
        {conversationActive && (
          <Banner type={BannerTypes.WARNING}>
          This conversation is currently active in a live session. You cannot send a reply until it
          settles -- your message is still here, try again in a moment.
          </Banner>
        )}
        <Thread ref={threadRef}>
          {isLoading && <EmptyThread>Loading conversation...</EmptyThread>}
          {!isLoading && messages.length === 0 && !optimisticBubble && (
            <EmptyThread>No messages in this conversation yet.</EmptyThread>
          )}
          {messages.map((event) => {
            const own = isOwnMessage(event.role);
            return (
              <Bubble own={own} key={`${event.timestamp}-${event.content}`}>
                {own
                  ? (() => {
                    const sender = resolveSenderUser(event, currentUser);
                    return sender
                      ? <AvatarDetails user={sender} description={senderLabel(event)} />
                      : <BubbleSender own={own}>{senderLabel(event)}</BubbleSender>;
                  })()
                  : <BubbleSender own={own}>{phoneNumber}</BubbleSender>}
                <BubbleContent own={own}>{event.content}</BubbleContent>
                <BubbleTime own={own}>{DisplayHelpers.formatDateTimeShort(event.timestamp)}</BubbleTime>
              </Bubble>
            );
          })}
          {optimisticBubble}
        </Thread>
        <Composer>
          <ComposerRow>
            <ComposerInput
              aria-label="Reply message"
              maxLength={MAX_SMS_LENGTH}
              placeholder={conversationActive
                ? 'Conversation is currently active...'
                : 'Type a reply...'}
              value={draft}
              disabled={locked}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
            />
            <ComposerActions>
              <Button
                disabled={locked || !draft.trim()}
                onClick={() => void handleSend()}
              >
                {sending ? 'Sending...' : 'Reply'}
              </Button>
              <Button styleType={StyleType.TERTIARY} onClick={handleClose}>Close</Button>
            </ComposerActions>
          </ComposerRow>
          <CharCount>{draft.length} / {MAX_SMS_LENGTH}</CharCount>
        </Composer>
      </Container>
    </Modal>
  );
}
